import { useState } from 'react';
import { NavbarConfig, NavLinkItem } from '@/lib/themeConfig';
import { SaveButton } from './GeneralSection';

interface Props { data: NavbarConfig; onChange: (data: NavbarConfig) => void; }

export default function NavbarSection({ data, onChange }: Props) {
  // Ensure "Ürünler" auto item exists in the list
  const ensureAutoItem = (links: NavLinkItem[]): NavLinkItem[] => {
    const hasAuto = links.some(l => l.isAuto);
    if (hasAuto) return links;
    return [{ label: 'Ürünler', href: '/urunler', isAuto: true }, ...links];
  };

  const [links, setLinks] = useState<NavLinkItem[]>(() => ensureAutoItem(data.links));
  const [saved, setSaved] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const handleSave = () => {
    onChange({ ...data, links });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const updateLink = (i: number, key: 'label' | 'href', val: string) => {
    setLinks(prev => prev.map((l, idx) => idx === i ? { ...l, [key]: val } : l));
  };

  const addLink = () => {
    setLinks(prev => [...prev, { label: 'Yeni Sayfa', href: '/' }]);
  };

  const removeLink = (i: number) => {
    if (links[i]?.isAuto) return; // auto item silinemez
    setLinks(prev => prev.filter((_, idx) => idx !== i));
  };

  // Drag & Drop
  const handleDragStart = (e: React.DragEvent, i: number) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIdx !== i) setDragOverIdx(i);
  };

  const handleDrop = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) {
      setDragIdx(null);
      setDragOverIdx(null);
      return;
    }
    const newLinks = [...links];
    const [moved] = newLinks.splice(dragIdx, 1);
    newLinks.splice(i, 0, moved);
    setLinks(newLinks);
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const moveUp = (i: number) => {
    if (i === 0) return;
    const n = [...links];
    [n[i - 1], n[i]] = [n[i], n[i - 1]];
    setLinks(n);
  };

  const moveDown = (i: number) => {
    if (i === links.length - 1) return;
    const n = [...links];
    [n[i], n[i + 1]] = [n[i + 1], n[i]];
    setLinks(n);
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-white rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-stone-100 pb-3">
          <h2 className="text-sm font-semibold text-stone-700">Navigasyon Linkleri</h2>
          <p className="text-xs text-stone-400 flex items-center gap-1">
            <i className="ri-drag-move-line" /> Sürükleyerek sırala
          </p>
        </div>

        <div className="space-y-2">
          {links.map((l, i) => (
            <div
              key={`${l.href}-${i}`}
              draggable
              onDragStart={e => handleDragStart(e, i)}
              onDragOver={e => handleDragOver(e, i)}
              onDrop={e => handleDrop(e, i)}
              onDragEnd={handleDragEnd}
              className={`flex gap-2 items-center p-2 rounded-xl border transition-all ${
                dragIdx === i
                  ? 'opacity-40 border-amber-300 bg-amber-50'
                  : dragOverIdx === i
                  ? 'border-amber-400 bg-amber-50/60 scale-[1.01]'
                  : l.isAuto
                  ? 'border-amber-200 bg-amber-50/50'
                  : 'border-stone-100 bg-stone-50 hover:border-stone-200'
              }`}
            >
              {/* Drag Handle */}
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 cursor-grab active:cursor-grabbing">
                <i className={`ri-drag-move-2-line text-sm ${l.isAuto ? 'text-amber-400' : 'text-stone-300'}`} />
              </div>

              {/* Label input veya sabit metin */}
              {l.isAuto ? (
                <div className="flex-1 flex items-center gap-2">
                  <div className="flex-1 px-3 py-2 text-sm text-amber-700 font-medium flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-lock-line text-xs text-amber-500" />
                    </div>
                    {l.label}
                    <span className="text-xs text-amber-400 font-normal ml-1">(Otomatik)</span>
                  </div>
                  <div className="flex-1 px-3 py-2 text-sm text-amber-400">{l.href}</div>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={l.label}
                    onChange={e => updateLink(i, 'label', e.target.value)}
                    placeholder="Menü Adı"
                    className="flex-1 px-3 py-2 text-sm bg-white rounded-lg border border-stone-200 focus:border-amber-500 focus:outline-none transition-all"
                  />
                  <div className="flex-1 flex items-center gap-0">
                    <input
                      type="text"
                      value={l.href}
                      onChange={e => updateLink(i, 'href', e.target.value)}
                      placeholder="/sayfa veya /p/slug"
                      className="flex-1 px-3 py-2 text-sm bg-white rounded-lg border border-stone-200 focus:border-amber-500 focus:outline-none transition-all"
                    />
                  </div>
                </>
              )}

              {/* Up/Down */}
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <button
                  onClick={() => moveUp(i)}
                  disabled={i === 0}
                  className="w-6 h-5 flex items-center justify-center text-stone-400 hover:text-stone-700 disabled:opacity-20 cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-up-s-line text-sm" />
                </button>
                <button
                  onClick={() => moveDown(i)}
                  disabled={i === links.length - 1}
                  className="w-6 h-5 flex items-center justify-center text-stone-400 hover:text-stone-700 disabled:opacity-20 cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-down-s-line text-sm" />
                </button>
              </div>

              {/* Delete */}
              {l.isAuto ? (
                <div className="w-8 h-8 flex-shrink-0" />
              ) : (
                <button
                  onClick={() => removeLink(i)}
                  className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer flex-shrink-0 transition-colors"
                >
                  <i className="ri-delete-bin-line" />
                </button>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={addLink}
          className="text-xs text-amber-600 hover:text-amber-700 cursor-pointer flex items-center gap-1 transition-colors"
        >
          <i className="ri-add-line" /> Link Ekle
        </button>
      </div>

      <SaveButton saved={saved} onSave={handleSave} />
    </div>
  );
}
